/**
 * FHIR Constants - Centralized configuration and magic strings
 *
 * This file contains all constants used across FHIR conversion services to eliminate magic strings and provide single
 * source of truth.
 */

import { TemplateStatus } from '../models/template.model';

// ============================================================================
// EXTENSION URL PREFIXES
// ============================================================================

/** Base URN prefix for all custom extensions */
export const EXT_PREFIX = 'urn:ccs:ext';

/** UI configuration extension key */
export const UI_CONFIG_KEY = 'ui';

/** Full UI config extension URL */
export const UI_CONFIG_EXT = `${EXT_PREFIX}:${UI_CONFIG_KEY}`;

/** Questionnaire URL prefix */
export const QUESTIONNAIRE_PREFIX = 'urn:ccs:q';

/** Publisher identifier */
export const FHIR_PUBLISHER = 'ccs:symphony';

// ============================================================================
// EXTENSION URL SUFFIXES (Human-readable keys)
// ============================================================================

export const EXT_KEYS = {
  /** Component type */
  COMPONENT: 'component',
  /** Package name */
  PACKAGE: 'package',
  /** Icon */
  ICON: 'icon',
  /** Category */
  CATEGORY: 'category',
  /** Description */
  DESCRIPTION: 'description',
  /** Placeholder */
  PLACEHOLDER: 'placeholder',
  /** Help text */
  HELP: 'help',
  /** Harmony properties */
  HARMONY: 'harmony',
  /** Color */
  COLOR: 'color',
  /** Change count */
  CHANGE_COUNT: 'changeCount',
  /** Created by */
  CREATED_BY: 'createdBy',
  /** Last modified by */
  LAST_MODIFIED_BY: 'lastModifiedBy',
  /** Published at */
  PUBLISHED_AT: 'publishedAt',
  /** Published by */
  PUBLISHED_BY: 'publishedBy',
  /** Property definitions */
  PROPERTY_DEFINITIONS: 'propertyDefinitions',
  /** Custom logic (visibility dependencies) */
  DEPENDENCIES: 'dependencies',
  /** Validation rules */
  SHOW_STATUS: 'showStatus',
  /** Hierarchy path (Section > Subsection > Control) */
  HIERARCHY: 'hierarchy',
} as const;

/**
 * Action name used in the dependencies extension to mark auto-generated subcontrol placement rules. Distinct from
 * 'visible' so these rules are unambiguously structural (not user-authored): field = parentLinkId value = optionValue
 * The runtime evaluator treats this the same as 'visible'.
 */
export const SUBCONTROL_DEPENDENCY_ACTION = 'subcontrol';

/**
 * Extension URL marking a flattened subcontrol item as the auto-generated "Other" text-input belonging to a
 * radio/checkbox control's "Other" option, distinguishing it from a manually-authored subcontrol. Presence of this
 * extension (valueBoolean: true) is the sole signal used by parsers/renderers to detect the new "Other" format; its
 * absence means the control is still using the legacy inline hasOther flag (backward compatibility).
 */
export const OTHER_INPUT_SUBCONTROL_EXT = `${EXT_PREFIX}:other-input`;

// ============================================================================
// COMPONENT NAMES
// ============================================================================

export const COMPONENTS = {
  /** Section component */
  SECTION: 'HxSection',
  /** Subsection component */
  SUBSECTION: 'HxSubsection',
} as const;

// ============================================================================
// AMENDMENT EXTENSION URLS
// ============================================================================

/** Extension URL for tagging QR items with amendment action (add/update/delete) */
export const AMENDMENT_ACTION_EXT = `${EXT_PREFIX}:amendment-action`;

// ============================================================================
// FHIR ITEM TYPES
// ============================================================================

export const FHIR_TYPES = {
  GROUP: 'group',
  STRING: 'string',
  TEXT: 'text',
  INTEGER: 'integer',
  DECIMAL: 'decimal',
  BOOLEAN: 'boolean',
  DATE: 'date',
  DATE_TIME: 'dateTime',
  TIME: 'time',
  CHOICE: 'choice',
  OPEN_CHOICE: 'open-choice',
  DISPLAY: 'display',
  ATTACHMENT: 'attachment',
  REFERENCE: 'reference',
  QUANTITY: 'quantity',
  URL: 'url',
} as const;

// ============================================================================
// QUESTIONNAIRE STATUS VALUES
// ============================================================================

export const QUESTIONNAIRE_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  RETIRED: 'retired',
  UNKNOWN: 'unknown',
} as const;

export type QuestionnaireStatus = (typeof QUESTIONNAIRE_STATUS)[keyof typeof QUESTIONNAIRE_STATUS];

/**
 * Maps template lifecycle statuses to questionnaire publishing statuses.
 *
 * @type {Record<TemplateStatus, QuestionnaireStatus>}
 */
export const TEMPLATE_STATUS_MAP: Record<TemplateStatus, QuestionnaireStatus> = {
  [TemplateStatus.Draft]: QUESTIONNAIRE_STATUS.DRAFT,
  [TemplateStatus.PendingApproval]: QUESTIONNAIRE_STATUS.DRAFT,
  [TemplateStatus.Published]: QUESTIONNAIRE_STATUS.ACTIVE,
  [TemplateStatus.Rejected]: QUESTIONNAIRE_STATUS.DRAFT,
  [TemplateStatus.Archived]: QUESTIONNAIRE_STATUS.RETIRED,
} as const;

/**
 * @example
 *   // QuestionnaireResponse → Observation mapping (define locally, no conductor changes needed)
 *   const RESPONSE_TO_OBSERVATION_MAP: Record<string, string> = {
 *     'in-progress': 'preliminary',
 *     completed: 'final',
 *     amended: 'amended',
 *   } as const;
 *
 *   const observationStatus = mapStatus(response.status, RESPONSE_TO_OBSERVATION_MAP) ?? 'unknown';
 */
export function mapStatus<T>(
  sourceStatus: number | string | undefined | null,
  mappingConfig: Record<number | string, T>
): T | undefined {
  // Handle null/undefined - return undefined
  if (sourceStatus === null || sourceStatus === undefined) {
    return undefined;
  }

  // Look up and return the mapped value (or undefined if not found)
  return mappingConfig[sourceStatus];
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULTS = {
  /** Default control type fallback */
  CONTROL_TYPE: 'text-input',
  /** Default category for control lookup */
  BASIC_CATEGORY: 'basic',
  /** Default version */
  VERSION: '1.0',
  /** Unknown user identifier */
  UNKNOWN_USER: 'unknown',
  /** Untitled template name */
  UNTITLED_TEMPLATE: 'Untitled Template',
  /** Untitled section name */
  UNTITLED_SECTION: 'Untitled Section',
} as const;

// ============================================================================
// EXCLUDED PROPERTIES (for extension building)
// ============================================================================

/** Properties to exclude when building control extensions */
export const EXCLUDED_CONTROL_PROPS = new Set([
  'id',
  'type',
  'icon',
  'question',
  'isRequired',
  'required',
  'hasOptions',
  'optionCount',
  'hasOther',
  'children',
  'component',
  'componentProps',
  'options',
  'label',
  'validationErrors',
  'propertyDefinitions',
  'fhirPath', // Exclude fhirPath - it goes in customLogic extension, not UI config
  '__parentOptionId', // Exclude - goes in ext:subcontrol-parent, not UI config
  '__parentLinkId', // Exclude - goes in ext:subcontrol-parent, not UI config
  '__isOtherInput', // Exclude - goes in ext:other-input, not UI config
]);

// ============================================================================
// SLUG CONFIGURATION
// ============================================================================

export const SLUG_CONFIG = {
  /** Characters used for unique slug generation */
  CHARS: 'abcdefghijklmnopqrstuvwxyz0123456789',
  /** Length of unique slug suffix */
  LENGTH: 3,
} as const;

// ============================================================================
// ERROR CODES
// ============================================================================

export const ERROR_CODES = {
  INVALID_INPUT: 'INVALID_INPUT',
  CONVERSION_ERROR: 'CONVERSION_ERROR',
  CONTROL_CONVERSION_ERROR: 'CONTROL_CONVERSION_ERROR',
  INVALID_RESOURCE_TYPE: 'INVALID_RESOURCE_TYPE',
  MISSING_STATUS: 'MISSING_STATUS',
  INVALID_STATUS: 'INVALID_STATUS',
  DUPLICATE_LINKID: 'DUPLICATE_LINKID',
  MISSING_LINKID: 'MISSING_LINKID',
  MISSING_TYPE: 'MISSING_TYPE',
  METADATA_NOT_FOUND: 'METADATA_NOT_FOUND',

  // FHIR R4 Questionnaire constraints (que-0 through que-13)
  QUE_0_INVALID_NAME: 'QUE_0_INVALID_NAME',
  QUE_1_GROUP_REQUIRES_ITEMS: 'QUE_1_GROUP_REQUIRES_ITEMS',
  QUE_1_DISPLAY_NO_ITEMS: 'QUE_1_DISPLAY_NO_ITEMS',
  QUE_3_DISPLAY_NO_CODE: 'QUE_3_DISPLAY_NO_CODE',
  QUE_4_OPTION_VS_VALUESET: 'QUE_4_OPTION_VS_VALUESET',
  QUE_5_INVALID_ANSWER_TYPE: 'QUE_5_INVALID_ANSWER_TYPE',
  QUE_6_DISPLAY_NO_REQUIRED_REPEATS: 'QUE_6_DISPLAY_NO_REQUIRED_REPEATS',
  QUE_7_EXISTS_REQUIRES_BOOLEAN: 'QUE_7_EXISTS_REQUIRES_BOOLEAN',
  QUE_8_NO_INITIAL_FOR_GROUP_DISPLAY: 'QUE_8_NO_INITIAL_FOR_GROUP_DISPLAY',
  QUE_9_DISPLAY_NO_READONLY: 'QUE_9_DISPLAY_NO_READONLY',
  QUE_10_MAXLENGTH_SIMPLE_TYPES: 'QUE_10_MAXLENGTH_SIMPLE_TYPES',
  QUE_11_OPTION_VS_INITIAL: 'QUE_11_OPTION_VS_INITIAL',
  QUE_12_ENABLE_BEHAVIOR_REQUIRED: 'QUE_12_ENABLE_BEHAVIOR_REQUIRED',
  QUE_13_MULTIPLE_INITIAL_REPEATS: 'QUE_13_MULTIPLE_INITIAL_REPEATS',
  INVALID_ITEM_TYPE: 'INVALID_ITEM_TYPE',

  // FHIR R4 QuestionnaireResponse constraints
  QRS_1_ANSWER_AND_ITEM: 'QRS_1_ANSWER_AND_ITEM',
  QRS_MISSING_LINKID: 'QRS_MISSING_LINKID',
  QRS_INVALID_ANSWER_VALUE: 'QRS_INVALID_ANSWER_VALUE',

  // FHIR R4 Patient constraints
  PAT_1_CONTACT_DETAILS: 'PAT_1_CONTACT_DETAILS',
  PAT_INVALID_GENDER: 'PAT_INVALID_GENDER',
  PAT_LINK_MISSING_OTHER: 'PAT_LINK_MISSING_OTHER',
  PAT_LINK_MISSING_TYPE: 'PAT_LINK_MISSING_TYPE',
  PAT_LINK_INVALID_TYPE: 'PAT_LINK_INVALID_TYPE',
  PAT_COMMUNICATION_MISSING_LANGUAGE: 'PAT_COMMUNICATION_MISSING_LANGUAGE',

  // FHIR R4 Practitioner constraints
  PRAC_INVALID_GENDER: 'PRAC_INVALID_GENDER',
  PRAC_QUALIFICATION_MISSING_CODE: 'PRAC_QUALIFICATION_MISSING_CODE',

  // FHIR R4 Encounter constraints
  ENC_STATUSHISTORY_MISSING_STATUS: 'ENC_STATUSHISTORY_MISSING_STATUS',
  ENC_STATUSHISTORY_MISSING_PERIOD: 'ENC_STATUSHISTORY_MISSING_PERIOD',
  ENC_CLASSHISTORY_MISSING_CLASS: 'ENC_CLASSHISTORY_MISSING_CLASS',
  ENC_CLASSHISTORY_MISSING_PERIOD: 'ENC_CLASSHISTORY_MISSING_PERIOD',
  ENC_DIAGNOSIS_MISSING_CONDITION: 'ENC_DIAGNOSIS_MISSING_CONDITION',
  ENC_LOCATION_MISSING_LOCATION: 'ENC_LOCATION_MISSING_LOCATION',
  ENC_INVALID_LOCATION_STATUS: 'ENC_INVALID_LOCATION_STATUS',

  // FHIR R4 Appointment constraints (app-1 through app-4)
  APP_1_PARTICIPANT_TYPE_OR_ACTOR: 'APP_1_PARTICIPANT_TYPE_OR_ACTOR',
  APP_2_START_END_BOTH: 'APP_2_START_END_BOTH',
  APP_3_START_END_STATUS: 'APP_3_START_END_STATUS',
  APP_4_CANCELATION_REASON_STATUS: 'APP_4_CANCELATION_REASON_STATUS',
  APP_PARTICIPANT_MISSING_STATUS: 'APP_PARTICIPANT_MISSING_STATUS',
  APP_PARTICIPANT_INVALID_STATUS: 'APP_PARTICIPANT_INVALID_STATUS',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
